/**
 * Theme provider — wires up MUI theme, direction, and emotion cache
 * based on the current language in Redux.
 *
 * Also updates the <html dir> and <html lang> attributes so native
 * elements (date inputs, scrollbars, etc.) render correctly.
 */

import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { CacheProvider } from '@emotion/react';
import { ThemeProvider as MuiThemeProvider, CssBaseline } from '@mui/material';

import { rtlCache } from './rtlCache';
import { ltrCache } from './ltrCache';
import { buildTheme } from './index';
import { selectLanguage } from '@/store/uiSlice';

/**
 * @param {{ children: React.ReactNode }} props
 */
export function ThemeProvider({ children }) {
  const language = useSelector(selectLanguage);
  const isRtl = language === 'fa';

  // Build theme once per language change
  const theme = useMemo(() => buildTheme(language), [language]);

  // Sync <html> attributes so the whole document reflects the direction
  useEffect(() => {
    document.documentElement.lang = language;
    document.documentElement.dir = isRtl ? 'rtl' : 'ltr';
  }, [language, isRtl]);

  const cache = isRtl ? rtlCache : ltrCache;

  return (
    <CacheProvider value={cache}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </CacheProvider>
  );
}

export default ThemeProvider;
